import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ImageIcon, Loader2Icon, MailIcon, PhoneIcon, ReceiptIcon, Trash2Icon, XIcon } from 'lucide-react';
import { Button, LinkButton, buttonClasses } from '../../components/ui/Button';
import { SelectField, TextField } from '../../components/ui/Field';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { StatusTimeline } from '../../components/admin/StatusTimeline';
import { useAdminData } from '../../contexts/AdminDataContext';
import { caseStatuses, photoStages } from '../../types/admin';
import type { CaseStatus, PhotoStage } from '../../types/admin';
import { validateImage } from '../../services/imageService';
import { formatDate, formatDateTime, formatPrice, telHref } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function CaseDetail() {
  const {
    caseId
  } = useParams();
  const navigate = useNavigate();
  const {
    cases,
    customerById,
    setStatus,
    addNote,
    deleteNote,
    addPhotos,
    deletePhoto,
    updateCase,
    deleteCase,
    invoiceForCase,
    createInvoiceFromCase
  } = useAdminData();
  const item = cases.find(entry => entry.id === caseId);
  const [note, setNote] = useState('');
  const [stage, setStage] = useState<PhotoStage>('Før');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  /** Valgte billeder der endnu ikke er uploadet — kan ses og fjernes først. */
  const [pending, setPending] = useState<{
    file: File;
    preview: string;
  }[]>([]);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');
  if (!item) return <Navigate to="/admin/sager" replace data-id="element-707" data-mp="3gg7k1iqbrbc8" />;
  const customer = customerById(item.customerId);
  const invoice = invoiceForCase(item.id);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-708" data-mp="3cgoxyjmrou7t">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-709" data-mp="3jiu7nakdv01m">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-710" data-mp="3tgi362ptcbku" />
        Sager
      </Link>

      <header className="mt-4 flex flex-wrap items-start justify-between gap-4" data-id="element-711" data-mp="3tenhgm6pwffp">
        <div data-id="element-712" data-mp="235c2ftdl9f9">
          <p className="text-sm text-ink-muted" data-id="element-713" data-mp="23nfpxidapx7">Sag #{item.number}</p>
          <h1 className="mt-1 font-display text-[clamp(1.7rem,4.5vw,2.4rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-714" data-mp="62w7iwtukdq4">
            {item.title}
          </h1>
          <p className="mt-1.5 text-[15px] text-ink-soft" data-id="element-715" data-mp="g1c0748ivv1q">
            {customer?.name ?? item.customerName}
            {item.fromWebsite && <span className="ml-2 rounded-full bg-canvas-deep px-2 py-0.5 text-xs text-ink-muted" data-id="element-716" data-mp="kzwz90c1g8ol">
                Fra hjemmesiden
              </span>}
          </p>
        </div>
        <div className="mt-2 flex items-center gap-3" data-id="element-717" data-mp="2msbic2qbjj6l">
          <StatusBadge status={item.status} data-id="element-718" data-mp="13x6yo3bzzbc2" />
          <button type="button" onClick={() => {
          if (window.confirm(`Slet sag #${item.number}? Handlingen kan ikke fortrydes.`)) {
            deleteCase(item.id);
            navigate('/admin/sager');
          }
        }} className="inline-flex h-9 items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-719" data-mp="13y8bodckq7wf">
            <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-720" data-mp="1iupkawtdp8fa" />
            Slet sag
          </button>
        </div>
      </header>

      {item.quoteId && <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-721" data-mp="18wjl4myyige4">
          <p className="text-[15px] text-ink" data-id="element-722" data-mp="2hiwl2c9h0hk">
            Oprettet fra tilbud: <span className="font-medium" data-id="element-723" data-mp="3tlzgzwd18vam">{item.quoteNumber}</span>
          </p>
          <Link to={`/admin/tilbud/${item.quoteId}`} className={buttonClasses('secondary', 'sm')} data-id="element-724" data-mp="1ldt0jxj95wdt">
            Åbn tilbud
          </Link>
        </section>}

      {/* Faktura: oprettes fra sagen, typisk når arbejdet er færdigt. */}
      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-725" data-mp="1ce5w12v88agj">
        <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-726" data-mp="1hd0rp5ealti2">
          <div data-id="element-727" data-mp="2510gw0vyhbuj">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-728" data-mp="251a4jhtj36x2">
              {invoice ? 'Faktura allerede oprettet' : 'Faktura'}
            </h2>
            <p className="mt-1 text-[15px] text-ink-soft" data-id="element-729" data-mp="1v3ckl6ipbwa9">
              {invoice ? `${invoice.invoiceNumber} · ${formatPrice(String(Math.round(invoice.total)))} · ${invoice.status}` : item.status === 'Færdig' ? 'Arbejdet er færdigt. Opret fakturaen ud fra sagen — alt kan rettes bagefter.' : 'Du kan oprette en fakturakladde, når du er klar. Alt kopieret kan rettes bagefter.'}
            </p>
          </div>
          {invoice ? <Link to={`/admin/fakturaer/${invoice.id}`} className={buttonClasses('secondary', 'md')} data-id="element-730" data-mp="c4y6k3qi7oyy">
              <ReceiptIcon className="h-4 w-4" aria-hidden="true" data-id="element-731" data-mp="q3616502ie6y" />
              Åbn faktura
            </Link> : <Button size="md" disabled={creatingInvoice} onClick={async () => {
          if (creatingInvoice) return;
          setCreatingInvoice(true);
          setInvoiceError('');
          const created = await createInvoiceFromCase(item);
          setCreatingInvoice(false);
          if (!created) {
            setInvoiceError('Fakturaen kunne ikke oprettes. Prøv igen.');
            return;
          }
          navigate(`/admin/fakturaer/${created.id}`);
        }} data-id="element-732" data-mp="l39svtkc80ll">
              {creatingInvoice ? <>
                  <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-733" data-mp="19rfcekzw8x48" />
                  Opretter …
                </> : 'Opret faktura'}
            </Button>}
        </div>
        {invoiceError && <p role="alert" className="mt-4 text-sm text-danger" data-id="element-734" data-mp="1oh3fnrrmy5ul">
            {invoiceError}
          </p>}
      </section>

      <section className="mt-6 rounded-card border border-line bg-white p-5 md:p-6" data-id="element-735" data-mp="qumhkkb2vo7s">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-736" data-mp="vrw51uewppsz">
          <SelectField id="sag-status" label="Status" className="w-full sm:w-64" value={item.status} onChange={event => setStatus(item.id, event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
          value: option,
          label: option
        }))} data-id="element-737" data-mp="10rsa7wzcbna4" />
        </div>
        <div className="mt-6" data-id="element-738" data-mp="2g77tt8osxwnd">
          <StatusTimeline status={item.status} data-id="element-739" data-mp="2galveepn61tr" />
        </div>
      </section>

      <div className="mt-6 grid gap-5 lg:grid-cols-[1.4fr_0.6fr]" data-id="element-740" data-mp="4knxdpt0wcfe">
        <div className="space-y-5" data-id="element-741" data-mp="jc336z7f30v">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-742" data-mp="kdp26hrd9xd">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-743" data-mp="3rwymio45sofb">Opgave</h2>
            <p className="mt-3 text-[16px] leading-relaxed text-ink-soft" data-id="element-744" data-mp="3my3qull3f5ds">
              {item.description || 'Ingen beskrivelse endnu.'}
            </p>
            <dl className="mt-5 grid gap-4 border-t border-line pt-5 sm:grid-cols-3" data-id="element-745" data-mp="3d0z4olr8z9wz">
              <div data-id="element-746" data-mp="3d3bwdqmt4yn8">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-747" data-mp="38z44xloyt4lb">Kategori</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-748" data-mp="340aszxpbtln9">{item.category}</dd>
              </div>
              <div data-id="element-749" data-mp="1780rkonoxbx2">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-750" data-mp="178kbgawwiadq">Adresse</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-751" data-mp="128egakrfa5qw">{item.address || '—'}</dd>
              </div>
              <div data-id="element-752" data-mp="b323dx3v62kc">
                <dt className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-753" data-mp="b4mxjlm9njho">Planlagt dato</dt>
                <dd className="mt-1 text-[15px] text-ink" data-id="element-754" data-mp="g1n4dyqxwq9u">{formatDate(item.plannedDate)}</dd>
              </div>
            </dl>
            <TextField id="sag-dato" label="Ret planlagt dato" type="date" className="mt-5 max-w-xs" value={item.plannedDate} onChange={event => updateCase(item.id, {
            plannedDate: event.target.value
          })} data-id="element-755" data-mp="1dijqp8bu4of" />
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-756" data-mp="3shqs1tksuy7c">
            <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-757" data-mp="3em52nlanmcee">Noter</h2>
            <form className="mt-4 flex flex-col gap-3 sm:flex-row" onSubmit={event => {
            event.preventDefault();
            if (!note.trim()) return;
            addNote(item.id, note.trim());
            setNote('');
          }} data-id="element-758" data-mp="3jjnfvnvpfds7">
              <label htmlFor="ny-note" className="sr-only" data-id="element-759" data-mp="32acg2w7dhkl2">
                Ny note
              </label>
              <input id="ny-note" value={note} onChange={event => setNote(event.target.value)} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." className="flex-1 rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-faint transition-colors duration-200 ease-smooth focus:border-accent focus:outline-none" data-id="element-760" data-mp="2x8vib9k6qe8p" />
              <Button type="submit" variant="dark" data-id="element-761" data-mp="3uz08rcugbc8n">
                Tilføj note
              </Button>
            </form>
            <ul className="mt-5 space-y-3" data-id="element-762" data-mp="3q0ylsgtwmsrw">
              {item.internalNotes.map(entry => <motion.li key={entry.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.22,
              ease
            }} className="flex items-start justify-between gap-3 rounded-xl bg-canvas p-4" data-id="element-763" data-mp="20lijnleee7bi">
                  <div data-id="element-764" data-mp="2h2cffe71ejpw">
                    <p className="text-xs text-ink-muted" data-id="element-765" data-mp="2c0ws1fhe014c">{formatDateTime(entry.createdAt)}</p>
                    <p className="mt-1 text-[15px] leading-relaxed text-ink" data-id="element-766" data-mp="2723gvtpkom7c">{entry.text}</p>
                  </div>
                  <button type="button" onClick={() => deleteNote(item.id, entry.id)} aria-label="Slet note" className="shrink-0 rounded-full p-1.5 text-ink-faint transition-colors duration-200 ease-smooth hover:bg-white hover:text-ink" data-id="element-767" data-mp="34qdfrzu2atu8">
                    <Trash2Icon className="h-4 w-4" aria-hidden="true" data-id="element-768" data-mp="2q0el471pu0ji" />
                  </button>
                </motion.li>)}
              {item.internalNotes.length === 0 && <li className="text-sm text-ink-muted" data-id="element-769" data-mp="3io23dffikf2z">Ingen noter endnu.</li>}
            </ul>
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-770" data-mp="3r0lwl0ro3b1a">
            <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-771" data-mp="3v04hhwjhnm44">
              <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-772" data-mp="3siapv1h33j1">Billeder</h2>
              <div className="flex items-center gap-2" data-id="element-773" data-mp="8to4lw6vcwu5">
                <div className="flex gap-1.5" role="group" aria-label="Vælg kategori for upload" data-id="element-774" data-mp="drpj03tco7za">
                  {photoStages.map(option => <button key={option} type="button" onClick={() => setStage(option)} aria-pressed={stage === option} className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ease-smooth ${stage === option ? 'border-accent/50 bg-accent/15 text-ink' : 'border-line bg-white text-ink-soft hover:border-ink/30'}`} data-id="element-775" data-mp="hrzz49auwerv">
                      {option}
                    </button>)}
                </div>
                <label htmlFor="sag-billeder" className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-full border border-line px-3.5 text-sm text-ink transition-colors duration-200 ease-smooth hover:border-ink/30" data-id="element-776" data-mp="lj7r4mnumr8y">
                  {uploading ? <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-777" data-mp="pjjcy2ry6xm0" /> : <ImageIcon className="h-4 w-4" aria-hidden="true" data-id="element-778" data-mp="14hbnshmjuxtv" />}
                  {uploading ? 'Uploader …' : 'Vælg billeder'}
                  <input id="sag-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
                  const input = event.target;
                  const files = Array.from(input.files ?? []);
                  input.value = '';
                  const problems = files.map(validateImage).filter(Boolean) as string[];
                  const accepted = files.filter(file => !validateImage(file));
                  setUploadError(problems.join(' '));
                  // Billederne lægges i kø, så de kan ses og fjernes før upload.
                  setPending(current => [...current, ...accepted.map(file => ({
                    file,
                    preview: URL.createObjectURL(file)
                  }))]);
                }} data-id="element-779" data-mp="368x3mt12y0yh" />
                </label>
              </div>
            </div>

            {pending.length > 0 && <div className="mt-5 rounded-xl border border-line bg-canvas p-4" data-id="element-780" data-mp="2nfztdcffxkcz">
                <p className="text-sm text-ink-soft" data-id="element-781" data-mp="nd7asyoqw5so">
                  {pending.length} {pending.length === 1 ? 'billede' : 'billeder'} klar til {stage}
                </p>
                <ul className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5" data-id="element-782" data-mp="edbygqjge4r2">
                  {pending.map((entry, index) => <li key={`${entry.file.name}-${index}`} className="relative" data-id="element-783" data-mp="9dfu2q6ugbaz" data-mpi={index}>
                      <img src={entry.preview} alt={entry.file.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-784" data-mp="4fnzfoeteb33" />
                      <button type="button" onClick={() => {
                  URL.revokeObjectURL(entry.preview);
                  setPending(current => current.filter((_, i) => i !== index));
                }} aria-label={`Fjern ${entry.file.name}`} className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-785" data-mp="3qvs9je1ubf90">
                        <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-786" data-mp="22b38p4oewvhm" />
                      </button>
                    </li>)}
                </ul>
                <div className="mt-4 flex flex-wrap gap-2" data-id="element-787" data-mp="19mnv8mkxil8e">
                  <Button size="sm" disabled={uploading} onClick={async () => {
                if (uploading) return;
                setUploading(true);
                setUploadError('');
                const result = await addPhotos(item.id, pending.map(entry => entry.file), stage);
                setUploading(false);
                if (!result.ok) {
                  // Køen bevares, så uploaden kan forsøges igen.
                  setUploadError(result.error ?? 'Billederne kunne ikke uploades.');
                  return;
                }
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
              }} data-id="element-788" data-mp="19o8qyfizca7u">
                    {uploading ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-789" data-mp="1facqh69ig69j" />
                        Uploader …
                      </> : uploadError ? 'Prøv upload igen' : `Upload til ${stage}`}
                  </Button>
                  <button type="button" disabled={uploading} onClick={() => {
                pending.forEach(entry => URL.revokeObjectURL(entry.preview));
                setPending([]);
                setUploadError('');
              }} className="inline-flex h-9 items-center rounded-full border border-line px-3.5 text-sm text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink disabled:opacity-50" data-id="element-790" data-mp="2zpedy0swujix">
                    Ryd
                  </button>
                </div>
              </div>}

            {uploadError && <p role="alert" className="mt-4 text-sm font-medium text-danger" data-id="element-791" data-mp="3qwwrz3k5cgod">
                {uploadError}
              </p>}

            <div className="mt-5 space-y-6" data-id="element-792" data-mp="3c7ih1r0x9r6v">
              {photoStages.map(group => {
              const photos = item.photos.filter(photo => photo.stage === group);
              return <div key={group} data-id="element-793" data-mp="8q097jl5gk6g">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-794" data-mp="8pqks0fr6l2v">{group}</h3>
                    {photos.length > 0 ? <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-795" data-mp="29m7u77d1e9wo">
                        {photos.map(photo => <li key={photo.id} className="relative" data-id="element-796" data-mp="24sljvlucii41">
                            <img src={photo.url} alt={`${group} — ${photo.name}`} className="aspect-square w-full rounded-xl object-cover" data-id="element-797" data-mp="20tw3dke0gkg6" />
                            <button type="button" onClick={() => deletePhoto(item.id, photo.id)} aria-label={`Slet billede ${photo.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-798" data-mp="2oiyoj27usvff">
                              <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-799" data-mp="137fcoxzs953e" />
                            </button>
                          </li>)}
                      </ul> : <p className="mt-2 text-sm text-ink-muted" data-id="element-800" data-mp="12zlhasapwd13">Ingen billeder.</p>}
                  </div>;
            })}
            </div>
          </section>
        </div>

        <aside className="space-y-5" data-id="element-801" data-mp="1bs5fxsr0ehlx">
          <section className="rounded-card border border-line bg-white p-6" data-id="element-802" data-mp="1br3xv4bksuup">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-803" data-mp="1gq70yj7zwr01">Kunde</h2>
            <p className="mt-3 font-display text-lg font-bold tracking-tight text-ink" data-id="element-804" data-mp="1lptbgkwfvt55">
              {customer?.name ?? item.customerName}
            </p>
            <ul className="mt-3 space-y-1.5 text-[15px] text-ink-soft" data-id="element-805" data-mp="1qmtjv2grh7zf">
              <li data-id="element-806" data-mp="3nj0vg7mzyyxd">{customer?.phone || '—'}</li>
              <li className="break-all" data-id="element-807" data-mp="3sg9a3y8xsktv">{customer?.email || '—'}</li>
              <li data-id="element-808" data-mp="3sin74cn6g742">{customer?.address || '—'}</li>
            </ul>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row" data-id="element-809" data-mp="20ghucck9u5a4">
              <LinkButton href={telHref(customer?.phone ?? '')} variant="secondary" size="sm" data-id="element-810" data-mp="24ga6t0cscvkr">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-811" data-mp="29g4uyw56f724" />
                Ring
              </LinkButton>
              <LinkButton href={`mailto:${customer?.email ?? ''}`} variant="secondary" size="sm" data-id="element-812" data-mp="2e6v1wdt93hyx">
                <MailIcon className="h-4 w-4" aria-hidden="true" data-id="element-813" data-mp="2j6hecl198u6o" />
                Send e-mail
              </LinkButton>
            </div>
            {customer && <Link to={`/admin/kunder/${customer.id}`} className="mt-4 inline-block text-sm font-medium text-accent hover:text-accent-deep" data-id="element-814" data-mp="2s6cwj9t89pg7">
                Se kundeprofil
              </Link>}
          </section>

          <section className="rounded-card border border-line bg-white p-6" data-id="element-815" data-mp="xu6cdlicvybr">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-muted" data-id="element-816" data-mp="12s7spyoedjjj">Økonomi</h2>
            <dl className="mt-4 divide-y divide-line/70 text-[15px]" data-id="element-817" data-mp="v9i0vl1mq77z">
              <div className="flex justify-between gap-3 py-2.5" data-id="element-818" data-mp="qgx90p2aa473">
                <dt className="text-ink-soft" data-id="element-819" data-mp="li3uquf6a95v">Timepris</dt>
                <dd className="font-medium text-ink" data-id="element-820" data-mp="linduegk73lh">{formatPrice(item.hourlyRate, 'kr./time')}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-821" data-mp="ggy0gs5v6du8">
                <dt className="text-ink-soft" data-id="element-822" data-mp="1e5q6ipyk0k3t">Fast tilbud</dt>
                <dd className="font-medium text-ink" data-id="element-823" data-mp="197y617jufpo0">{formatPrice(item.fixedPrice)}</dd>
              </div>
              <div className="flex justify-between gap-3 py-2.5" data-id="element-824" data-mp="148bwp974ytkh">
                <dt className="text-ink-soft" data-id="element-825" data-mp="14aojq0rr3u4e">Anslået beløb</dt>
                <dd className="font-medium text-ink" data-id="element-826" data-mp="1068f02tvisqr">{formatPrice(item.estimate)}</dd>
              </div>
            </dl>
            <TextField id="sag-slutbeloeb" label="Endeligt beløb (kr.)" className="mt-4" inputMode="numeric" placeholder="Udfyldes når opgaven er færdig" value={item.finalAmount} onChange={event => updateCase(item.id, {
            finalAmount: event.target.value
          })} data-id="element-827" data-mp="3p05mgo45oemy" />
          </section>
        </aside>
      </div>
    </div>;
}